import { l as DocContext, k as jsxRuntimeExports, t as textRendererStyle, n as DynamicMath } from "./doenetml-a_KitGQm.js";
import React__default, { useContext, useRef, useEffect } from "react";
import { j as jxg, s as syncLayer, a as syncWithLabelToggle, b as syncLabelMaskCssStyle, c as computeLabelMaskCssStyle, d as attachLabelHoverHighlight } from "./JSXGraphRenderer-BMkZb_8Q.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-Ca2HvWV4.js";
import { BoardContext, LINE_LAYER_OFFSET } from "./graph-gLVvVe_r.js";
import { ChoiceInputInlineContext } from "./choiceInput-D_Fr5wBX.js";
import { g as getPatternFillAttributes } from "./fillPatterns-BQpfOUtY.js";
import { r as resolveFillColor, a as resolveLineColor, b as resolveCanvasColor } from "./styleColors-Cknqcr0q.js";
const angle = React__default.memo(function Angle(props) {
  let { id, SVs } = useDoenetRenderer(props);
  const board = useContext(BoardContext);
  const choiceInputInlineContext = useContext(ChoiceInputInlineContext);
  let point1JXG = useRef(null);
  let point2JXG = useRef(null);
  let point3JXG = useRef(null);
  let angleJXG = useRef(null);
  let previousWithLabel = useRef(null);
  const { darkMode } = useContext(DocContext) || {};
  useEffect(() => {
    return () => {
      deleteGraphicalObject();
    };
  }, []);
  function deleteGraphicalObject() {
    if (point1JXG.current !== null) {
      board?.removeObject(angleJXG.current);
      angleJXG.current = null;
      board?.removeObject(point1JXG.current);
      point1JXG.current = null;
      board?.removeObject(point2JXG.current);
      point2JXG.current = null;
      board?.removeObject(point3JXG.current);
      point3JXG.current = null;
    }
  }
  function createAngleJXG() {
    if (board === null) {
      return null;
    }
    if (SVs.numericalPoints.length !== 3 || SVs.numericalPoints.some((x) => x.length !== 2) || !(Number.isFinite(SVs.numericalRadius) && SVs.numericalRadius > 0)) {
      return null;
    }
    const fillAttributes = getPatternFillAttributes({
      defsEl: board.renderer.defs,
      boardId: board.container.id,
      fillStyle: SVs.selectedStyle.fillStyle ?? "solid",
      fillColor: resolveFillColor(SVs.selectedStyle, darkMode),
      fillOpacity: SVs.selectedStyle.fillOpacity,
      canvasColor: resolveCanvasColor(darkMode),
      fillPatternOpacity: SVs.selectedStyle.fillPatternOpacity
    });
    var jsxAngleAttributes = {
      name: SVs.labelForGraph,
      visible: !SVs.hidden,
      withLabel: SVs.labelForGraph !== "",
      fixed: true,
      layer: 10 * SVs.layer + LINE_LAYER_OFFSET,
      radius: SVs.numericalRadius,
      fillColor: fillAttributes.fillColor,
      fillOpacity: fillAttributes.fillOpacity,
      strokeColor: resolveLineColor(SVs.selectedStyle, darkMode),
      highlight: false,
      orthoType: SVs.emphasizeRightAngle ? "square" : "sector"
    };
    jsxAngleAttributes.label = {
      highlight: false,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      highlightStrokeOpacity: 1
    };
    if (SVs.labelHasLatex) {
      jsxAngleAttributes.label.useMathJax = true;
    }
    previousWithLabel.current = SVs.labelForGraph !== "";
    let through;
    if (SVs.swapPointOrder) {
      through = [
        [...SVs.numericalPoints[2]],
        [...SVs.numericalPoints[1]],
        [...SVs.numericalPoints[0]]
      ];
    } else {
      through = [
        [...SVs.numericalPoints[0]],
        [...SVs.numericalPoints[1]],
        [...SVs.numericalPoints[2]]
      ];
    }
    let jsxPointAttributes = {
      visible: false
    };
    point1JXG.current = board.create(
      "point",
      through[0],
      jsxPointAttributes
    );
    point2JXG.current = board.create(
      "point",
      through[1],
      jsxPointAttributes
    );
    point3JXG.current = board.create(
      "point",
      through[2],
      jsxPointAttributes
    );
    const newAngleJXG = board.create(
      "angle",
      [point1JXG.current, point2JXG.current, point3JXG.current],
      jsxAngleAttributes
    );
    attachLabelHoverHighlight({
      hoverTargetJXG: newAngleJXG,
      getLabelJXG: () => angleJXG.current?.label,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      board
    });
    return newAngleJXG;
  }
  if (SVs.hidden) {
    return null;
  }
  if (board) {
    if (angleJXG.current === null) {
      angleJXG.current = createAngleJXG();
    } else if (SVs.numericalPoints.length !== 3 || SVs.numericalPoints.some((x) => x.length !== 2) || !(Number.isFinite(SVs.numericalRadius) && SVs.numericalRadius > 0)) {
      deleteGraphicalObject();
    } else {
      let through;
      if (SVs.swapPointOrder) {
        through = [
          [...SVs.numericalPoints[2]],
          [...SVs.numericalPoints[1]],
          [...SVs.numericalPoints[0]]
        ];
      } else {
        through = [
          [...SVs.numericalPoints[0]],
          [...SVs.numericalPoints[1]],
          [...SVs.numericalPoints[2]]
        ];
      }
      angleJXG.current.point2.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        through[0]
      );
      angleJXG.current.point1.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        through[1]
      );
      angleJXG.current.point3.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        through[2]
      );
      angleJXG.current.setAttribute({
        radius: SVs.numericalRadius,
        visible: !SVs.hidden
      });
      syncLayer(angleJXG.current, SVs.layer, LINE_LAYER_OFFSET);
      const fillAttributes = getPatternFillAttributes({
        defsEl: board.renderer.defs,
        boardId: board.container.id,
        fillStyle: SVs.selectedStyle.fillStyle ?? "solid",
        fillColor: resolveFillColor(SVs.selectedStyle, darkMode),
        fillOpacity: SVs.selectedStyle.fillOpacity,
        canvasColor: resolveCanvasColor(darkMode),
        fillPatternOpacity: SVs.selectedStyle.fillPatternOpacity
      });
      const angleFillColor = fillAttributes.fillColor;
      if (angleJXG.current.visProp.fillcolor !== angleFillColor) {
        angleJXG.current.visProp.fillcolor = angleFillColor;
      }
      if (angleJXG.current.visProp.fillopacity !== fillAttributes.fillOpacity) {
        angleJXG.current.visProp.fillopacity = fillAttributes.fillOpacity;
      }
      const angleLineColor = resolveLineColor(
        SVs.selectedStyle,
        darkMode
      );
      if (angleJXG.current.visProp.strokecolor !== angleLineColor) {
        angleJXG.current.visProp.strokecolor = angleLineColor;
      }
      angleJXG.current.name = SVs.labelForGraph;
      syncWithLabelToggle(
        angleJXG.current,
        SVs.labelForGraph,
        previousWithLabel
      );
      angleJXG.current.visProp.orthotype = SVs.emphasizeRightAngle ? "square" : "sector";
      angleJXG.current.needsUpdate = true;
      angleJXG.current.update();
      if (angleJXG.current.hasLabel && angleJXG.current.label) {
        angleJXG.current.label.needsUpdate = true;
        syncLabelMaskCssStyle(angleJXG.current.label, SVs.layer, {
          maskLabel: SVs.maskLabel
        });
        angleJXG.current.label.update();
      }
      board.updateRenderer();
    }
    return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id });
  }
  const mathJaxify = "\\(" + SVs.latexForRenderer + "\\)";
  const style = !choiceInputInlineContext.inOption ? textRendererStyle(darkMode ?? "light", SVs.selectedStyle) : void 0;
  return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { style, id, children: /* @__PURE__ */ jsxRuntimeExports.jsx(DynamicMath, { latex: mathJaxify }) });
});
export {
  angle as default
};
